import { useEffect, useRef, useState } from "react";
import { Send, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { MicButton } from "@/components/VoiceInput";
import { cn } from "@/lib/utils";

const SUGGEST = ["How much 4 sq mm KEI is available?", "Which sizes are running low?", "What did each party take this month?", "Which drums have less than 20 m left?"];
const LANGS = [["en-IN", "English"], ["hi-IN", "हिंदी"], ["kn-IN", "ಕನ್ನಡ"]];
const newSid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

async function streamAsk(body, onToken) {
  const go = () => fetch(`${API}/ai/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  let res = await go();
  if (res.status === 401) { await api.post("/auth/refresh"); res = await go(); }
  if (!res.ok || !res.body) throw new Error("AI is not available right now");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error(j.error);
      onToken(j.t);
    }
  }
}

export default function AskAI() {
  const { user } = useAuth();
  const key = `roxy-ai-session-${user?.id}`;
  const [sid, setSid] = useState(() => localStorage.getItem(key) || newSid());
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("en-IN");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    let live = true;
    setReady(false);
    localStorage.setItem(key, sid);
    api.get("/ai/history", { params: { session_id: sid } }).then((r) => { if (live) setMsgs(r.data); }).finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [sid, key]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || busy || !ready) return;
    setQ("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    const add = (t) => setMsgs((m) => {
      const last = m[m.length - 1];
      return last?.role === "assistant" ? [...m.slice(0, -1), { ...last, text: last.text + t }] : m;
    });
    try { await streamAsk({ question, session_id: sid }, add); } catch (e) { add(`\n${e.message}`); }
    setBusy(false);
  };
  return (
    <div className="max-w-3xl">
      <PageHeader title="Ask AI" subtitle="Ask about your stock, parties and today's work in English, Hindi or Kannada. Answers use only your live data.">
        <Button variant="outline" disabled={busy} onClick={() => { setSid(newSid()); setMsgs([]); }} data-testid="ai-new-chat-btn"><Plus className="h-4 w-4 mr-1" />New chat</Button>
      </PageHeader>
      <div className="rounded-lg border bg-card p-4 min-h-[45vh] space-y-3" data-testid="ai-messages">
        {!msgs.length && <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkles className="h-4 w-4 text-red-600" />Try one of these</div>
          <div className="flex flex-wrap gap-2">{SUGGEST.map((s, i) => <button key={s} type="button" disabled={!ready || busy} onClick={() => ask(s)} className="rounded-full border px-3 py-1.5 text-sm hover:border-red-600 transition-colors" data-testid={`ai-suggest-${i}`}>{s}</button>)}</div>
        </div>}
        {msgs.map((m, i) => (
          <div key={i} className={cn("max-w-[90%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap", m.role === "user" ? "ml-auto bg-[#0B1530] text-white" : "bg-muted")} data-testid={`ai-msg-${m.role}-${i}`}>
            {m.text || (busy && i === msgs.length - 1 ? "Thinking…" : "")}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form className="mt-3 flex gap-2 sticky bottom-20 lg:bottom-4" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 shrink-0 rounded-md border bg-background px-1 text-sm" data-testid="ai-lang-select">{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type your question…" className="h-10 min-w-0" data-testid="ai-question-input" />
        <MicButton lang={lang} onText={(t) => ask(t)} testid="ai-mic-btn" className="h-10 w-10 shrink-0" />
        <Button type="submit" disabled={busy || !ready || !q.trim()} className="h-10 shrink-0 bg-red-600 hover:bg-red-700 text-white" data-testid="ai-send-btn"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
